/**
 * Ein Datumsfeld, das wie ein Kalender aussieht und sich wie einer bedienen lässt.
 *
 * Darunter steckt weiterhin das native <input autoComplete="off" type="date"> – es bringt
 * Tastatur, Landesformat und den Kalender des Systems mit, und nichts
 * Selbstgebautes kommt daran heran. Zwei Dinge fehlten ihm trotzdem:
 *
 *   1. Es sah aus wie ein Textfeld. In Safari steht dort «tt.mm.jjjj» ohne
 *      jedes Zeichen, dass etwas aufgeht – und wer nicht weiss, dass er
 *      klicken kann, tippt. Deshalb das Kalendersymbol rechts.
 *
 *   2. Der Kalender ging nur über ein winziges Feld am rechten Rand auf.
 *      Jetzt öffnet ihn ein Klick irgendwo im Feld, über showPicker(). Wo der
 *      Browser das nicht kennt, bleibt alles beim Alten – das Feld
 *      funktioniert dann wie bisher und nicht schlechter.
 *
 *   3. Der Kalender blieb nach der Wahl eines Tages offen. Gemeldet vom CEO,
 *      Desktop und iPhone: Ein Tag ist gewählt, und der Kalender steht weiter
 *      da, bis man daneben tippt. Jetzt verlässt das Feld nach einer Wahl im
 *      Kalender den Fokus – damit schliesst ihn jeder Browser, auch Safari
 *      auf dem iPhone. Nur nach einer Wahl IM KALENDER: Wer das Datum mit der
 *      Tastatur Stelle für Stelle tippt, wird nicht mitten im Jahr aus dem
 *      Feld geworfen.
 */
import { useRef, type CSSProperties } from 'react'

/*
  showPicker gibt es seit 2022 in allen grossen Browsern, aber nicht in jeder
  Typdefinition – und in älteren Safari-Versionen gar nicht. Deshalb kein
  eigener Elementtyp, sondern eine schmale Prüfung an der einen Stelle, die
  es aufruft.
*/
type MitPicker = { showPicker?: () => void }

export function Datumsfeld({
  id,
  value,
  onChange,
  min,
  max,
  style
}: {
  id: string
  value: string
  onChange: (wert: string) => void
  min?: string
  max?: string
  style?: CSSProperties
}): JSX.Element {
  const feld = useRef<HTMLInputElement | null>(null)
  /* Ist der Kalender gerade von uns geöffnet worden? Tippen setzt das zurück. */
  const kalenderOffen = useRef(false)

  function kalenderOeffnen(): void {
    const el = feld.current
    if (!el || el.disabled || el.readOnly) return
    kalenderOffen.current = true
    try {
      ;(el as HTMLInputElement & MitPicker).showPicker?.()
    } catch {
      /* Manche Browser erlauben showPicker nur aus einer echten Geste heraus.
         Dann bleibt das Feld ein normales Datumsfeld – kein Grund für einen
         Fehler auf dem Bildschirm. */
    }
  }

  return (
    <div className="o-datum" style={style}>
      <input
        autoComplete="off"
        id={id}
        ref={feld}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          onChange(e.target.value)
          /* Ein ganzer Tag aus dem Kalender: schliessen. */
          if (kalenderOffen.current && /^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) {
            kalenderOffen.current = false
            e.target.blur()
          }
        }}
        onKeyDown={() => {
          kalenderOffen.current = false
        }}
        onBlur={() => {
          kalenderOffen.current = false
        }}
        onClick={kalenderOeffnen}
      />
      <button
        type="button"
        className="o-datum-knopf"
        tabIndex={-1}
        aria-label="Kalender öffnen"
        onClick={kalenderOeffnen}
      >
        ▦
      </button>
    </div>
  )
}
